import { deleteField } from "firebase/firestore";

/**
 * Build the per-slot patch for the regular Submit-button vote flow.
 *
 * The pre-fix submit path sent `votes: draftVotes` straight through
 * `setDoc({merge: true})`. That has two problems on the votes map:
 *   1. Firestore's per-key merge does NOT delete missing keys — so a slot
 *      the user toggled off in the draft never actually clears in Firestore.
 *      The next snapshot re-renders the persisted vote and the clear "undoes
 *      itself" from the user's perspective.
 *   2. voteAutoSource is never written, so manually-cast votes lack the
 *      explicit-null manual-lock marker. The auto-vote engine's manual-lock
 *      invariant (voteAutoSource[slot] === null → don't override) and the
 *      Discord enrichment's isManualVote detection both rely on that null.
 *      Without it, manual votes are vulnerable to engine override and the
 *      Discord panel renders 💡 (mark suggestion) instead of 👤 (your vote).
 *
 * This helper produces a patch that:
 *   - For every slot whose draft value differs from persisted (i.e., user
 *     changed it):
 *       - if cleared (draft == null): votes[slot] = deleteField, voteAutoSource[slot] = null
 *       - if set/changed: votes[slot] = level, voteAutoSource[slot] = null
 *   - For unchanged slots: emit nothing (leave Firestore as-is).
 *
 * Note: noTimesWork=true callers should pass `draftVotes: {}` so every prior
 * vote diffs to a clear.
 *
 * @param {Object} params
 * @param {Object<string, string>} params.persistedVotes  current Firestore votes map
 * @param {Object<string, string>} params.draftVotes      votes the user is submitting
 * @returns {{ votes: Object, voteAutoSource: Object, hasChanges: boolean }}
 */
export function buildVoteSubmitPatch({
  persistedVotes = {},
  draftVotes = {},
} = {}) {
  const votes = {};
  const voteAutoSource = {};
  const allSlotIds = new Set([
    ...Object.keys(persistedVotes || {}),
    ...Object.keys(draftVotes || {}),
  ]);

  let hasChanges = false;
  for (const slotId of allSlotIds) {
    const before = persistedVotes?.[slotId] ?? null;
    const after = draftVotes?.[slotId] ?? null;
    if (before === after) continue;
    hasChanges = true;
    if (after == null) {
      votes[slotId] = deleteField();
      voteAutoSource[slotId] = null;
    } else {
      votes[slotId] = after;
      voteAutoSource[slotId] = null;
    }
  }

  return { votes, voteAutoSource, hasChanges };
}

/**
 * Attach the per-slot vote maps to a Firestore vote-write payload ONLY when the
 * submission actually changed something.
 *
 * WHY THIS EXISTS: the vote write goes through `setDoc(..., { merge: true })`. In
 * both the client and Admin SDKs an EMPTY map (`{}`) under merge is a
 * field-level REPLACE that CLEARS the field — it is NOT a no-op. So writing
 * `votes: {}` (exactly what `buildVoteSubmitPatch` returns when nothing changed)
 * silently wipes the user's entire vote set. This hit prod on 2026-07-07: a user
 * re-opened a scheduler they had already voted on and pressed Submit again
 * without editing, and every vote was deleted while `userTouchedSlots` survived.
 * Gating the vote maps on `hasChanges` makes an unchanged re-submit a true no-op
 * for the vote data (the metadata in `base` still refreshes).
 *
 * @param {Object} base                    non-vote fields of the write payload
 * @param {Object} patch                   result of buildVoteSubmitPatch
 * @param {Object} patch.votes             per-slot vote diff (may be empty)
 * @param {Object} [patch.voteAutoSource]  per-slot manual-lock diff (omit for guests)
 * @param {boolean} patch.hasChanges       whether any slot changed
 * @returns {Object} a new payload; votes/voteAutoSource attached iff hasChanges
 */
export function withVoteMapsIfChanged(base, { votes, voteAutoSource, hasChanges } = {}) {
  if (!hasChanges) return { ...base };
  const next = { ...base, votes };
  if (voteAutoSource !== undefined) {
    next.voteAutoSource = voteAutoSource;
  }
  return next;
}
